import { useEffect, useRef, useMemo, useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import { answerPropType } from '@aytm/app/stats/page/question-views/components/lib/prop-types'
import classNames from 'classnames'

import { useComponentWidth } from '@aytm/app/stats/page/question-views/components/charts/lib/hooks'
import {
  useExportableChart,
  svgToPngDataUrl
} from '@aytm/app/stats/page/question-views/components/charts/lib/exportable'

import { drawChart, clearChart } from '@aytm/app/stats/page/question-views/cjc/components/chart/lib/chart'
import { isSummarisedPayload } from '@aytm/app/stats/page/question-views/cjc/lib/model-data'
import {
  calcData,
  calcMainPackages,
  findElements,
  getNextPackage,
  getPrevPackage
} from '@aytm/app/stats/page/question-views/cjc/components/chart/lib/chart-utils'
import { formatSVG, formatAllSVG } from '@aytm/app/stats/page/question-views/cjc/components/chart/lib/export-svg'

import { formatSVG as serializeSVG } from '@aytm/app/stats/lib/render/svg-export'
import { useRenderGate } from '@aytm/app/stats/page/question-views/components/charts/placeholders/lib/render-gate'
import ExportDropdown from '@aytm/app/stats/components/export-dropdown/ExportDropdown'
import ChartNote from '@aytm/app/stats/page/question-views/vk/components/chart-note/ChartNote'

import { formatAmounts } from '@aytm/app/stats/lib/util/helpers'

import style from '@aytm/app/stats/page/question-views/cjc/components/chart/styles/Chart.module.scss'

const CHART_WIDTH = 658
export const CHART_HEIGHT = 400

const SETTINGS = {
  pw: 50, // percent width
  sw: 50, // sidebar width
  hw: 20, // hidden batch width
  pp: 20, // percent padding
  bp: 9, // batch padding
  ah: 22, // answer variant height
  sh: 32, // selected height
  fs1: 12, // font size 1
  fs2: 14 // font size 2
}

const ANIMATION_TIME = 300

// Walking to a distant package is eased rather than linear: the steps start slow and
// accelerate, so the columns read as spinning up. Ported from setCurrentPackage.
const STEP_EASING = v => v ** 4
// Floor per step, so even the first hop is perceptible.
const STEP_MIN_MS = 100

// Below this the model is too underpowered to trust; the chart still draws, with a warning.
const MIN_COMPLETES = 400

// Numbers each mounted chart, so the ids inside its svg never collide with another conjoint's.
let chartSeq = 0

const EXPORT_OPTIONS = [
  { text: 'Download PNG image', key: 'png' },
  { text: 'Download JPEG image', key: 'jpeg' },
  { text: 'Download PDF document', key: 'pdf' },
  { text: 'Download EPS file', key: 'eps' }
]

const ConjointChart = ({
  questionId,
  answer,
  batches,
  features,
  mode,
  persona,
  hiddenBatches,
  currentPackage,
  packageRankSharesArr,
  packageRankScoresArr,
  scale,
  center,
  sortOrder,
  colorTheme,
  chartLegendMode,
  onPackageChange,
  onHiddenBatchesChange,
  onExport,
  onChartReady
}) => {
  const svgRef = useRef(null)
  const chartIdRef = useRef(null)
  if (null === chartIdRef.current) chartIdRef.current = ++chartSeq
  const cachedStatRef = useRef({})
  const prevStateIdRef = useRef('')
  const hoveredBatchRef = useRef(0)
  const scrollingBatchRef = useRef(0)
  // Per-step values the eased walk mutates; drawChart reads them at draw time.
  const animationTimeRef = useRef(ANIMATION_TIME)
  // Timers of the walks in flight, one list per column, `all` for a whole-package walk.
  const stepTimersRef = useRef({})
  // The package every walk in flight steps, so a second column's walk cannot rewind the first.
  const walkingRef = useRef(null)
  // Stable object handed to block.coffee once; its methods read live refs/state so
  // the lazy PPTX calls (block.formatSVG / formatAllSVG) reflect the current chart.
  const apiRef = useRef({})

  const { componentRef, width } = useComponentWidth({ defaultWidth: CHART_WIDTH })
  const [isReady, setIsReady] = useState(false)

  const settings = useMemo(
    () => ({
      ...SETTINGS,
      width: width,
      height: CHART_HEIGHT
    }),
    [width]
  )

  const mainPackages = useMemo(
    () => calcMainPackages(answer, mode, persona, packageRankSharesArr, packageRankScoresArr),
    [answer, mode, persona, packageRankSharesArr, packageRankScoresArr]
  )

  const currentPackageArray = useMemo(() => currentPackage.split('.').map(v => parseInt(v, 10)), [currentPackage])

  const clearStepTimers = useCallback((...keys) => {
    const timers = stepTimersRef.current
    ;(keys.length ? keys : Object.keys(timers)).forEach(key => {
      timers[key]?.forEach(clearTimeout)
      delete timers[key]
    })
    if (!Object.keys(timers).length) walkingRef.current = null
  }, [])

  // A walk in flight must not outlive the chart, or it would keep pushing package changes.
  useEffect(() => clearStepTimers, [clearStepTimers])

  // Cleared on unmount only: a redraw updates the svg in place, so clearing between draws would
  // rebuild every shape and restart its animation from the previous position.
  useEffect(() => () => clearChart(svgRef.current), [])

  const chartState = useMemo(() => {
    if (!answer || !batches?.length) return null
    // the layout indexes the coefficient and tick-mark arrays positionally, so a summarised payload
    // has nothing to lay out and no position to lay it out at
    if (isSummarisedPayload(answer, persona)) return null

    return calcData({
      answer,
      batches,
      mode,
      persona,
      currentPackage: currentPackageArray,
      hiddenBatches,
      sortOrder,
      center,
      scale,
      colorTheme,
      chartLegendMode,
      settings,
      cachedStat: cachedStatRef.current,
      prevStateId: prevStateIdRef.current,
      onPrevStateIdChange: id => {
        prevStateIdRef.current = id
      }
    })
  }, [
    answer,
    batches,
    mode,
    persona,
    currentPackageArray,
    hiddenBatches,
    sortOrder,
    center,
    scale,
    colorTheme,
    chartLegendMode,
    settings
  ])

  // `silent` is the animated form: walk the intermediate packages on an eased schedule
  // instead of cutting to the destination. The wheel and the sidebar arrows pass false
  // because they already move one package at a time.
  //
  // Steps are scheduled faster than the model round-trip returns, so a walk steps a package held
  // here rather than re-reading the prop, which would rewind it. Walks in different columns share
  // that package and each moves only its own column, so a second pick lets the first one finish.
  const handlePackageChange = useCallback(
    (bPosition, avPosition, packages, silent = true) => {
      const walking = walkingRef.current || [...currentPackageArray]
      const key = packages ? 'all' : bPosition
      let ids
      let duration

      if (packages) {
        ids = [...packages]
        duration = ANIMATION_TIME
      } else {
        // Every variant between where we are and what was clicked, current one dropped.
        ids = findElements(chartState?.ws, bPosition, walking[bPosition - 1], avPosition)
        ids.shift()
        duration = Math.min(ANIMATION_TIME, 50 * ids.length)
        scrollingBatchRef.current = bPosition
      }

      if (!ids.length) return

      // a whole-package move replaces every column, and a column's new pick replaces its old walk
      if (packages) clearStepTimers()
      else clearStepTimers(key, 'all')
      walkingRef.current = walking

      const applyStep = p => {
        if (Array.isArray(p)) {
          walking.splice(0, walking.length, ...p)
        } else {
          walking[bPosition - 1] = p
        }
        onPackageChange(walking.join('.'))
      }

      if (!silent) {
        applyStep(ids[ids.length - 1])
        clearStepTimers(key)
        return
      }

      // Total eased weight, so the per-step delays sum to `duration` whatever the length.
      const weight = ids.reduce((acc, _p, i) => acc + STEP_EASING(i + 1), 0)
      const stepMs = duration / weight

      const timers = (stepTimersRef.current[key] = [])
      let delay = 0
      ids.forEach((p, i) => {
        const at = STEP_MIN_MS + stepMs * STEP_EASING(i + 1)
        delay += at
        timers.push(
          setTimeout(() => {
            animationTimeRef.current = at
            applyStep(p)
          }, delay)
        )
      })

      timers.push(
        setTimeout(() => {
          clearStepTimers(key)
          if (!walkingRef.current) {
            animationTimeRef.current = ANIMATION_TIME
            scrollingBatchRef.current = 0
          }
        }, delay + ANIMATION_TIME)
      )
    },
    [currentPackageArray, chartState, onPackageChange, clearStepTimers]
  )

  // silent=true is the arrow click (step one ranked package); the wheel passes false.
  const handleNextPackage = useCallback(
    (silent = true) => {
      const nextPkg = getNextPackage({
        answer,
        mode,
        currentPackage: currentPackageArray,
        mainPackages,
        persona,
        silent,
        packageRankSharesArr,
        packageRankScoresArr
      })

      if (nextPkg.join(',') !== currentPackageArray.join(',')) {
        handlePackageChange(0, 0, [nextPkg], false)
      }
    },
    [
      answer,
      mode,
      currentPackageArray,
      mainPackages,
      persona,
      packageRankSharesArr,
      packageRankScoresArr,
      handlePackageChange
    ]
  )

  const handlePrevPackage = useCallback(
    (silent = true) => {
      const prevPkg = getPrevPackage({
        answer,
        mode,
        currentPackage: currentPackageArray,
        mainPackages,
        persona,
        silent,
        packageRankSharesArr,
        packageRankScoresArr
      })

      if (prevPkg.join(',') !== currentPackageArray.join(',')) {
        handlePackageChange(0, 0, [prevPkg], false)
      }
    },
    [
      answer,
      mode,
      currentPackageArray,
      mainPackages,
      persona,
      packageRankSharesArr,
      packageRankScoresArr,
      handlePackageChange
    ]
  )

  const handleBestPackage = useCallback(() => {
    if (mainPackages[0] && currentPackageArray.join(',') !== mainPackages[0].join(',')) {
      handlePackageChange(0, 0, [mainPackages[0]])
    }
  }, [currentPackageArray, mainPackages, handlePackageChange])

  const handleWorstPackage = useCallback(() => {
    if (mainPackages[4] && currentPackageArray.join(',') !== mainPackages[4].join(',')) {
      handlePackageChange(0, 0, [mainPackages[4]])
    }
  }, [currentPackageArray, mainPackages, handlePackageChange])

  const handleHiddenBatchToggle = useCallback(
    batchId => {
      const newHiddenBatches = [...hiddenBatches]
      const index = newHiddenBatches.indexOf(batchId)

      if (index >= 0) {
        newHiddenBatches.splice(index, 1)
      } else {
        newHiddenBatches.push(batchId)
      }

      onHiddenBatchesChange(newHiddenBatches)
    },
    [hiddenBatches, onHiddenBatchesChange]
  )

  const onExportClick = useCallback(
    (fileType, callback) => {
      const data = (null !== svgRef.current ? svgRef.current.cloneNode(true) : void 0) || null

      onExport(
        {
          fileType
        },
        data,
        callback
      )
    },
    [onExport]
  )

  useEffect(() => {
    if (!svgRef.current || !chartState) return

    drawChart(svgRef.current, {
      chartId: chartIdRef.current,
      state: chartState,
      settings,
      mode,
      answer,
      persona,
      currentPackage: currentPackageArray,
      mainPackages,
      animationTime: animationTimeRef.current,
      hoveredBatch: hoveredBatchRef,
      scrollingBatch: scrollingBatchRef,
      onPackageChange: handlePackageChange,
      onHiddenBatchToggle: handleHiddenBatchToggle,
      onNextPackage: handleNextPackage,
      onPrevPackage: handlePrevPackage,
      onBestPackage: handleBestPackage,
      onWorstPackage: handleWorstPackage
    })

    setIsReady(true)
  }, [
    chartState,
    settings,
    mode,
    answer,
    persona,
    currentPackageArray,
    mainPackages,
    handlePackageChange,
    handleHiddenBatchToggle,
    handleNextPackage,
    handlePrevPackage,
    handleBestPackage,
    handleWorstPackage
  ])

  // drawChart settles the svg before it flips isReady; with nothing to draw there is no such moment
  const chartRendered = useRenderGate()
  useEffect(() => {
    if (isReady || !chartState) chartRendered()
  }, [isReady, chartState, chartRendered])

  // Headless export: single-output. drawChart is synchronous and, on first render,
  // old-state === current-state (prevStateId is empty) so animateChart is a no-op —
  // the svg is at final positions once isReady flips. Hook must sit above the early
  // return to respect rules-of-hooks.
  useExportableChart({
    deps: [isReady, chartState],
    prepareForExport: async () => {
      const svgEl = svgRef.current
      if (!svgEl) throw new Error('CJ: SVG ref not attached')
      const w = svgEl.width.baseVal.value || CHART_WIDTH
      const h = svgEl.height.baseVal.value || CHART_HEIGHT
      const svgString =
        '<?xml version="1.0" encoding="UTF-8" standalone="no"?>' + new XMLSerializer().serializeToString(svgEl)
      return [await svgToPngDataUrl(svgString, w, h)]
    }
  })

  // Live-page PPTX bridge (pptx.coffee -> block.formatSVG / formatAllSVG). Reassign
  // every render so the closures capture the current chartState / props; the object
  // identity stays stable so onChartReady only needs to hand it over once.
  apiRef.current.formatSVG = () =>
    svgRef.current ? serializeSVG(formatSVG(svgRef.current, chartState, settings)) : null
  // a summarised payload has no layout, and the per-persona svgs are built from it
  apiRef.current.formatAllSVG = packageType =>
    svgRef.current && chartState
      ? formatAllSVG({
          svgNode: svgRef.current,
          currentState: chartState,
          answer,
          batches,
          mode,
          currentPackage: currentPackageArray,
          hiddenBatches,
          sortOrder,
          center,
          scale,
          colorTheme,
          chartLegendMode,
          packageType,
          settings
        }).map(serializeSVG)
      : []

  apiRef.current.isDrawn = () => Boolean(svgRef.current && chartState)

  // pptx rasterises the exported svg onto a canvas and needs its pixel size. This used to be
  // read off the legacy chart object's state/settings, which the React chart does not create.
  // Mirrors the width the layout settles on: the left percent gutter plus every column.
  apiRef.current.getExportSize = () =>
    chartState
      ? {
          width: (chartState.bs || []).reduce((acc, cur) => acc + cur.cw, settings.pw),
          height: settings.height
        }
      : null

  useEffect(() => {
    onChartReady?.(apiRef.current)
  }, [onChartReady])

  if (!answer || !batches?.length) {
    return null
  }

  return (
    <div ref={componentRef} className={classNames(style.chart)}>
      {features?.export?.disabled ? null : (
        <div className={style.export}>
          <ExportDropdown className={style['export-dropdown']} options={EXPORT_OPTIONS} onExport={onExportClick} />
        </div>
      )}
      <div id={questionId == null ? undefined : `chart_question_${questionId}`}>
        <svg
          ref={svgRef}
          className={classNames(style.svg, { [style.ready]: isReady })}
          width={settings.width}
          height={settings.height}
          viewBox={`0 0 ${settings.width} ${settings.height}`}
        />
      </div>
      {MIN_COMPLETES > (answer.total || 0) && (
        <ChartNote error>
          <i className="icon-aytmfd-help-v2" />
          <span>
            <b>Attention:</b>&nbsp;This experiment requires at least {MIN_COMPLETES} completes. You are looking at{' '}
            {formatAmounts(answer, 'total')} responses only.
          </span>
        </ChartNote>
      )}
    </div>
  )
}

ConjointChart.propTypes = {
  questionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  answer: answerPropType,
  batches: PropTypes.array,
  features: PropTypes.object,
  mode: PropTypes.string,
  persona: PropTypes.number,
  hiddenBatches: PropTypes.array,
  currentPackage: PropTypes.string,
  packageRankSharesArr: PropTypes.array,
  packageRankScoresArr: PropTypes.array,
  scale: PropTypes.bool,
  center: PropTypes.bool,
  sortOrder: PropTypes.string,
  colorTheme: PropTypes.array,
  chartLegendMode: PropTypes.string,
  onPackageChange: PropTypes.func,
  onHiddenBatchesChange: PropTypes.func,
  onExport: PropTypes.func,
  onChartReady: PropTypes.func
}

ConjointChart.defaultProps = {
  hiddenBatches: [],
  sortOrder: 'iasc',
  center: true,
  scale: true,
  chartLegendMode: 'long'
}

ConjointChart.displayName = 'ConjointChart'

export default ConjointChart
